import React, { useState, useRef, useEffect, useCallback } from 'react';
import Icon from '../../../components/AppIcon';
import { slideService } from '../../../services/slideService';

// ─── Constants ────────────────────────────────────────────────────────────────
const SLIDE_W = 960;
const SLIDE_H = 540; // 16:9

const FONT_FAMILIES = [
  { value: 'Inter, sans-serif', label: 'Inter' },
  { value: 'Georgia, serif', label: 'Georgia' },
  { value: 'Arial, sans-serif', label: 'Arial' },
  { value: 'Verdana, sans-serif', label: 'Verdana' },
  { value: '"Courier New", monospace', label: 'Courier New' },
  { value: '"Times New Roman", serif', label: 'Times New Roman' },
  { value: '"Trebuchet MS", sans-serif', label: 'Trebuchet MS' },
  { value: 'Impact, sans-serif', label: 'Impact' },
];

const FONT_SIZES = [12, 14, 16, 18, 20, 24, 28, 32, 36, 40, 48, 56, 64, 72, 96];

const BG_PRESETS = [
  { label: 'Dark Slate', value: '#0f172a' },
  { label: 'Slate 900', value: '#1e293b' },
  { label: 'Gray 900', value: '#111827' },
  { label: 'Black', value: '#000000' },
  { label: 'Navy', value: '#1a1f3c' },
  { label: 'Deep Blue', value: '#0c1445' },
  { label: 'Forest', value: '#0a2e1a' },
  { label: 'Dark Red', value: '#2d0a0a' },
  { label: 'White', value: '#ffffff' },
  { label: 'Light Gray', value: '#f1f5f9' },
  { label: 'Warm White', value: '#fafaf8' },
  { label: 'Amber Dark', value: '#451a03' },
];

const ELEMENT_TYPES = {
  text: { icon: 'Type', label: 'Text', color: 'bg-sky-100 text-sky-700' },
  image: { icon: 'Image', label: 'Image', color: 'bg-violet-100 text-violet-700' },
  shape: { icon: 'Square', label: 'Shape', color: 'bg-green-100 text-green-700' },
};

const ALIGN_OPTIONS = [
  { value: 'left', icon: 'AlignLeft' },
  { value: 'center', icon: 'AlignCenter' },
  { value: 'right', icon: 'AlignRight' },
];

const SHAPE_TYPES = [
  { value: 'rectangle', label: 'Rectangle' },
  { value: 'rounded', label: 'Rounded Rect' },
  { value: 'circle', label: 'Circle/Oval' },
  { value: 'line', label: 'Horizontal Line' },
];

function clamp(val, min, max) {
  return Math.max(min, Math.min(max, val));
}

function genId() {
  return `el-${Date.now()}-${Math.random()?.toString(36)?.slice(2, 7)}`;
}

// ─── Resize Handle Positions ──────────────────────────────────────────────────
const HANDLES = [
  { id: 'nw', cx: 0, cy: 0 },
  { id: 'n',  cx: 0.5, cy: 0 },
  { id: 'ne', cx: 1, cy: 0 },
  { id: 'e',  cx: 1, cy: 0.5 },
  { id: 'se', cx: 1, cy: 1 },
  { id: 's',  cx: 0.5, cy: 1 },
  { id: 'sw', cx: 0, cy: 1 },
  { id: 'w',  cx: 0, cy: 0.5 },
];

const CURSOR_MAP = {
  nw: 'nw-resize', n: 'n-resize', ne: 'ne-resize',
  e: 'e-resize', se: 'se-resize', s: 's-resize',
  sw: 'sw-resize', w: 'w-resize',
};

// ─── Element Renderer (for canvas) ───────────────────────────────────────────
const ElementRenderer = ({ el, scale }) => {
  const s = scale || 1;
  if (el?.type === 'text') {
    return (
      <div
        style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: el?.vAlign === 'top' ? 'flex-start' : el?.vAlign === 'bottom' ? 'flex-end' : 'center',
          justifyContent: el?.align === 'left' ? 'flex-start' : el?.align === 'right' ? 'flex-end' : 'center',
          padding: `${4 * s}px`,
          boxSizing: 'border-box',
          overflow: 'hidden',
          pointerEvents: 'none',
          userSelect: 'none',
        }}
      >
        <span
          style={{
            fontFamily: el?.fontFamily || 'Inter, sans-serif',
            fontSize: `${(el?.fontSize || 24) * s}px`,
            fontWeight: el?.bold ? 'bold' : 'normal',
            fontStyle: el?.italic ? 'italic' : 'normal',
            textDecoration: el?.underline ? 'underline' : 'none',
            color: el?.color || '#ffffff',
            textAlign: el?.align || 'center',
            lineHeight: 1.3,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            textShadow: el?.shadow ? '1px 2px 4px rgba(0,0,0,0.6)' : 'none',
          }}
        >
          {el?.text || 'Text'}
        </span>
      </div>
    );
  }
  if (el?.type === 'image') {
    return (
      <div style={{ width: '100%', height: '100%', overflow: 'hidden', pointerEvents: 'none' }}>
        {el?.url ? (
          <img
            src={el?.url}
            alt={el?.alt || ''}
            draggable={false}
            style={{
              width: '100%',
              height: '100%',
              objectFit: el?.objectFit || 'cover',
              display: 'block',
              opacity: el?.opacity !== undefined ? el?.opacity : 1,
              borderRadius: el?.borderRadius ? `${el?.borderRadius * (scale || 1)}px` : 0,
            }}
          />
        ) : (
          <div style={{
            width: '100%', height: '100%',
            background: '#374151',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#9ca3af', fontSize: `${12 * s}px`,
          }}>
            No image
          </div>
        )}
      </div>
    );
  }
  if (el?.type === 'shape') {
    const shapeType = el?.shapeType || 'rectangle';
    const borderRadius =
      shapeType === 'rounded' ? `${8 * s}px` :
      shapeType === 'circle' ? '50%' : '0';
    const height = shapeType === 'line' ? `${Math.max(2, (el?.lineThickness || 4) * s)}px` : '100%';
    return (
      <div style={{
        width: '100%',
        height: height,
        marginTop: shapeType === 'line' ? 'auto' : 0,
        marginBottom: shapeType === 'line' ? 'auto' : 0,
        background: el?.fill || '#f59e0b',
        borderRadius,
        border: el?.strokeWidth ? `${el?.strokeWidth * s}px solid ${el?.stroke || '#ffffff'}` : 'none',
        opacity: el?.opacity !== undefined ? el?.opacity : 1,
        pointerEvents: 'none',
        boxSizing: 'border-box',
      }} />
    );
  }
  return null;
};

// ─── Slide Canvas ─────────────────────────────────────────────────────────────
const SlideCanvas = ({ elements, backgroundColor, selectedId, onSelect, onUpdateElement, onDeselect }) => {
  const containerRef = useRef(null);
  const [containerW, setContainerW] = useState(640);
  const dragRef = useRef(null);

  useEffect(() => {
    const obs = new ResizeObserver(entries => {
      for (let e of entries) setContainerW(e.contentRect.width);
    });
    if (containerRef?.current) obs?.observe(containerRef?.current);
    return () => obs?.disconnect();
  }, []);

  const scale = containerW / SLIDE_W;
  const canvasH = SLIDE_H * scale;

  const toSlide = useCallback((clientX, clientY) => {
    const rect = containerRef?.current?.getBoundingClientRect();
    if (!rect) return { x: 0, y: 0 };
    return {
      x: (clientX - rect?.left) / scale,
      y: (clientY - rect?.top) / scale,
    };
  }, [scale]);

  const onMouseDown = useCallback((e, elId, handle) => {
    e?.stopPropagation();
    e?.preventDefault();
    const el = elements?.find(el => el?.id === elId);
    if (!el) return;
    onSelect(elId);
    const pos = toSlide(e?.clientX, e?.clientY);
    dragRef.current = {
      elId,
      handle,
      startX: pos?.x,
      startY: pos?.y,
      origX: el?.x,
      origY: el?.y,
      origW: el?.w,
      origH: el?.h,
    };
  }, [elements, onSelect, toSlide]);

  const onMouseMove = useCallback((e) => {
    if (!dragRef?.current) return;
    const pos = toSlide(e?.clientX, e?.clientY);
    const ds = dragRef?.current;
    const dx = pos?.x - ds?.startX;
    const dy = pos?.y - ds?.startY;
    const MIN = 20;

    const updated = elements?.map(el => {
      if (el?.id !== ds?.elId) return el;
      if (!ds?.handle) {
        // Move
        return {
          ...el,
          x: clamp(ds?.origX + dx, 0, SLIDE_W - el?.w),
          y: clamp(ds?.origY + dy, 0, SLIDE_H - el?.h),
        };
      }
      // Resize
      let { x, y, w, h } = { x: ds?.origX, y: ds?.origY, w: ds?.origW, h: ds?.origH };
      const handle = ds?.handle;
      if (handle?.includes('e')) w = clamp(ds?.origW + dx, MIN, SLIDE_W - x);
      if (handle?.includes('s')) h = clamp(ds?.origH + dy, MIN, SLIDE_H - y);
      if (handle?.includes('w')) {
        const nw = clamp(ds?.origW - dx, MIN, ds?.origX + ds?.origW);
        x = ds?.origX + ds?.origW - nw;
        w = nw;
      }
      if (handle?.includes('n')) {
        const nh = clamp(ds?.origH - dy, MIN, ds?.origY + ds?.origH);
        y = ds?.origY + ds?.origH - nh;
        h = nh;
      }
      return { ...el, x, y, w, h };
    });
    onUpdateElement(updated);
  }, [elements, toSlide, onUpdateElement]);

  const onMouseUp = useCallback(() => {
    dragRef.current = null;
  }, []);

  useEffect(() => {
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, [onMouseMove, onMouseUp]);

  return (
    <div ref={containerRef} style={{ width: '100%' }}>
      <div
        style={{
          position: 'relative',
          width: '100%',
          height: canvasH,
          background: backgroundColor || '#0f172a',
          overflow: 'hidden',
          borderRadius: 8,
          cursor: 'default',
          userSelect: 'none',
        }}
        onMouseDown={(e) => {
          if (e?.target === e?.currentTarget) onDeselect();
        }}
      >
        {/* Grid overlay (subtle) */}
        <div style={{
          position: 'absolute', inset: 0, pointerEvents: 'none', opacity: 0.04,
          backgroundImage: 'linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)',
          backgroundSize: `${40 * scale}px ${40 * scale}px`,
        }} />

        {/* Elements */}
        {(elements || [])?.map((el) => {
          const isSelected = el?.id === selectedId;
          const left = el?.x * scale;
          const top = el?.y * scale;
          const width = el?.w * scale;
          const height = el?.h * scale;

          return (
            <div
              key={el?.id}
              style={{
                position: 'absolute',
                left, top, width, height,
                outline: isSelected ? '2px solid #f59e0b' : '1px solid transparent',
                outlineOffset: isSelected ? 1 : 0,
                cursor: 'move',
                zIndex: isSelected ? 10 : (el?.zIndex || 1),
                boxShadow: isSelected ? '0 0 0 1px rgba(245,158,11,0.3)' : 'none',
              }}
              onMouseDown={(e) => onMouseDown(e, el?.id, null)}
            >
              <ElementRenderer el={el} scale={scale} />
              {/* Resize handles */}
              {isSelected && HANDLES?.map(h => (
                <div
                  key={h?.id}
                  style={{
                    position: 'absolute',
                    left: `${h?.cx * 100}%`,
                    top: `${h?.cy * 100}%`,
                    width: 10,
                    height: 10,
                    background: '#f59e0b',
                    border: '2px solid #fff',
                    borderRadius: 2,
                    transform: 'translate(-50%, -50%)',
                    cursor: CURSOR_MAP?.[h?.id],
                    zIndex: 20,
                    boxShadow: '0 1px 3px rgba(0,0,0,0.5)',
                  }}
                  onMouseDown={(e) => onMouseDown(e, el?.id, h?.id)}
                />
              ))}
            </div>
          );
        })}

        {/* Empty state */}
        {(!elements || elements?.length === 0) && (
          <div style={{
            position: 'absolute', inset: 0,
            display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center',
            color: 'rgba(255,255,255,0.25)', pointerEvents: 'none',
          }}>
            <svg width={48} height={48} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1}>
              <rect x={3} y={3} width={18} height={18} rx={2} />
              <path d="M3 9h18M9 21V9" />
            </svg>
            <p style={{ marginTop: 12, fontSize: 13 }}>Add elements using the toolbar below</p>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Properties Panel ─────────────────────────────────────────────────────────
const PropertiesPanel = ({ el, onUpdate, onDelete, onDuplicate, onBringForward, onSendBackward }) => {
  const [imgUploading, setImgUploading] = useState(false);
  const [imgError, setImgError] = useState(null);
  const fileInputRef = useRef(null);

  const set = (key, val) => onUpdate({ ...el, [key]: val });

  const handleImageUpload = async (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;
    setImgUploading(true);
    setImgError(null);
    const result = await slideService?.uploadImage(file);
    setImgUploading(false);
    if (result?.success) {
      set('url', result?.url);
    } else {
      setImgError(result?.error || 'Upload failed');
    }
  };

  const handlePaste = useCallback(async (e) => {
    if (el?.type !== 'image') return;
    const items = Array.from(e?.clipboardData?.items || []);
    const imgItem = items?.find(i => i?.type?.startsWith('image/'));
    if (!imgItem) return;
    e?.preventDefault();
    const file = imgItem?.getAsFile();
    if (!file) return;
    setImgUploading(true);
    const result = await slideService?.uploadImage(file);
    setImgUploading(false);
    if (result?.success) set('url', result?.url);
  }, [el]);

  useEffect(() => {
    if (el?.type !== 'image') return;
    document.addEventListener('paste', handlePaste);
    return () => document.removeEventListener('paste', handlePaste);
  }, [el?.type, handlePaste]);

  if (!el) {
    return (
      <div className="flex flex-col items-center justify-center h-32 text-gray-400 text-xs text-center p-4">
        <Icon name="MousePointer" size={20} color="#d1d5db" />
        <p className="mt-2">Select an element on the canvas to edit its properties</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Element actions */}
      <div className="flex items-center gap-1.5 flex-wrap">
        <button onClick={onDuplicate} title="Duplicate" className="flex items-center gap-1 px-2 py-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-600 text-xs font-medium transition-colors">
          <Icon name="Copy" size={13} /> Duplicate
        </button>
        <button onClick={onBringForward} title="Bring Forward" className="flex items-center gap-1 px-2 py-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-600 text-xs font-medium transition-colors">
          <Icon name="ArrowUp" size={13} /> Forward
        </button>
        <button onClick={onSendBackward} title="Send Backward" className="flex items-center gap-1 px-2 py-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-600 text-xs font-medium transition-colors">
          <Icon name="ArrowDown" size={13} /> Backward
        </button>
        <button onClick={onDelete} title="Delete" className="flex items-center gap-1 px-2 py-1.5 rounded-lg bg-red-50 hover:bg-red-100 text-red-500 text-xs font-medium transition-colors ml-auto">
          <Icon name="Trash2" size={13} /> Delete
        </button>
      </div>
      {/* Position & Size */}
      <div>
        <label className="block text-xs font-semibold text-gray-600 mb-2 uppercase tracking-wide">Position & Size</label>
        <div className="grid grid-cols-2 gap-2">
          {[
            { label: 'X', key: 'x', min: 0, max: SLIDE_W },
            { label: 'Y', key: 'y', min: 0, max: SLIDE_H },
            { label: 'W', key: 'w', min: 20, max: SLIDE_W },
            { label: 'H', key: 'h', min: 20, max: SLIDE_H },
          ]?.map(({ label, key, min, max }) => (
            <div key={key}>
              <label className="block text-xs text-gray-500 mb-0.5">{label}</label>
              <input
                type="number"
                min={min}
                max={max}
                value={Math.round(el?.[key] ?? 0)}
                onChange={e => set(key, parseInt(e?.target?.value) || 0)}
                className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-amber-500"
              />
            </div>
          ))}
        </div>
      </div>
      {/* Opacity */}
      <div>
        <label className="block text-xs font-semibold text-gray-600 mb-1">
          Opacity: <span className="text-amber-600">{Math.round((el?.opacity !== undefined ? el?.opacity : 1) * 100)}%</span>
        </label>
        <input
          type="range" min={0} max={1} step={0.05}
          value={el?.opacity !== undefined ? el?.opacity : 1}
          onChange={e => set('opacity', parseFloat(e?.target?.value))}
          className="w-full accent-amber-500"
        />
      </div>
      {/* TEXT properties */}
      {el?.type === 'text' && (
        <>
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Text Content</label>
            <textarea
              value={el?.text || ''}
              onChange={e => set('text', e?.target?.value)}
              rows={3}
              placeholder="Enter text..."
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 resize-none"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Font Family</label>
            <select
              value={el?.fontFamily || 'Inter, sans-serif'}
              onChange={e => set('fontFamily', e?.target?.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
            >
              {FONT_FAMILIES?.map(f => (
                <option key={f?.value} value={f?.value} style={{ fontFamily: f?.value }}>{f?.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Font Size</label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                min={8}
                max={200}
                value={el?.fontSize || 24}
                onChange={e => set('fontSize', parseInt(e?.target?.value) || 24)}
                className="w-20 border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
              />
              <div className="flex flex-wrap gap-1 flex-1">
                {[16, 24, 32, 48, 64, 96]?.map(sz => (
                  <button
                    key={sz}
                    onClick={() => set('fontSize', sz)}
                    className={`px-2 py-1 rounded text-xs font-medium border transition-colors ${
                      (el?.fontSize || 24) === sz ? 'border-amber-500 bg-amber-50 text-amber-700' : 'border-gray-200 text-gray-600 hover:border-gray-300'
                    }`}
                  >
                    {sz}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-2">Style</label>
            <div className="flex gap-2">
              <button
                onClick={() => set('bold', !el?.bold)}
                className={`flex-1 py-2 rounded-lg border text-sm font-bold transition-colors ${el?.bold ? 'border-amber-500 bg-amber-50 text-amber-700' : 'border-gray-200 text-gray-600 hover:border-gray-300'}`}
              >B</button>
              <button
                onClick={() => set('italic', !el?.italic)}
                className={`flex-1 py-2 rounded-lg border text-sm italic transition-colors ${el?.italic ? 'border-amber-500 bg-amber-50 text-amber-700' : 'border-gray-200 text-gray-600 hover:border-gray-300'}`}
              >I</button>
              <button
                onClick={() => set('underline', !el?.underline)}
                className={`flex-1 py-2 rounded-lg border text-sm underline transition-colors ${el?.underline ? 'border-amber-500 bg-amber-50 text-amber-700' : 'border-gray-200 text-gray-600 hover:border-gray-300'}`}
              >U</button>
              <button
                onClick={() => set('shadow', !el?.shadow)}
                title="Text Shadow"
                className={`flex-1 py-2 rounded-lg border text-xs transition-colors ${el?.shadow ? 'border-amber-500 bg-amber-50 text-amber-700' : 'border-gray-200 text-gray-600 hover:border-gray-300'}`}
              >
                <Icon name="Layers" size={14} className="mx-auto" />
              </button>
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-2">Alignment</label>
            <div className="flex gap-2">
              {ALIGN_OPTIONS?.map(a => (
                <button
                  key={a?.value}
                  onClick={() => set('align', a?.value)}
                  className={`flex-1 py-2 rounded-lg border flex items-center justify-center transition-colors ${
                    (el?.align || 'center') === a?.value ? 'border-amber-500 bg-amber-50 text-amber-700' : 'border-gray-200 text-gray-600 hover:border-gray-300'
                  }`}
                >
                  <Icon name={a?.icon} size={14} />
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-2">Vertical Align</label>
            <div className="flex gap-2">
              {[
                { value: 'top', icon: 'AlignStartVertical' },
                { value: 'middle', icon: 'AlignCenterVertical' },
                { value: 'bottom', icon: 'AlignEndVertical' },
              ]?.map(a => (
                <button
                  key={a?.value}
                  onClick={() => set('vAlign', a?.value)}
                  className={`flex-1 py-2 rounded-lg border flex items-center justify-center transition-colors ${
                    (el?.vAlign || 'middle') === a?.value ? 'border-amber-500 bg-amber-50 text-amber-700' : 'border-gray-200 text-gray-600 hover:border-gray-300'
                  }`}
                >
                  <Icon name={a?.icon} size={14} />
                </button>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-3">
            <label className="text-xs font-semibold text-gray-600 flex-shrink-0">Text Color</label>
            <input
              type="color"
              value={el?.color || '#ffffff'}
              onChange={e => set('color', e?.target?.value)}
              className="w-9 h-9 rounded-lg cursor-pointer border border-gray-300 p-0.5"
            />
            <input
              type="text"
              value={el?.color || '#ffffff'}
              onChange={e => { if (/^#[0-9a-fA-F]{0,6}$/?.test(e?.target?.value)) set('color', e?.target?.value); }}
              className="flex-1 border border-gray-300 rounded-lg px-2 py-1.5 text-xs font-mono focus:outline-none focus:ring-2 focus:ring-amber-500"
            />
          </div>

          <div className="flex items-center gap-3">
            <label className="text-xs font-semibold text-gray-600 flex-shrink-0">Background</label>
            <input
              type="color"
              value={el?.bgColor || '#00000000'}
              onChange={e => set('bgColor', e?.target?.value)}
              className="w-9 h-9 rounded-lg cursor-pointer border border-gray-300 p-0.5"
            />
            <button
              onClick={() => set('bgColor', 'transparent')}
              className="text-xs text-gray-500 hover:text-red-500 transition-colors"
            >Clear</button>
          </div>
        </>
      )}
      {/* IMAGE properties */}
      {el?.type === 'image' && (
        <>
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-2">Image Source</label>
            {el?.url && (
              <div className="mb-2 relative rounded-lg overflow-hidden h-40 bg-gray-100">
                <img src={el?.url} alt="Preview" className="w-full h-full object-cover" />
                <button
                  onClick={() => set('url', '')}
                  className="absolute top-1.5 right-1.5 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center"
                >
                  <Icon name="X" size={10} />
                </button>
              </div>
            )}
            {imgError && <p className="text-xs text-red-500 mb-1">{imgError}</p>}
            <input ref={fileInputRef} type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
            <button
              onClick={() => fileInputRef?.current?.click()}
              disabled={imgUploading}
              className="w-full border-2 border-dashed border-gray-300 rounded-lg p-3 text-xs text-gray-500 hover:border-amber-400 hover:text-amber-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {imgUploading ? (
                <><div className="animate-spin rounded-full h-3 w-3 border-2 border-amber-500 border-t-transparent" /> Uploading...</>
              ) : (
                <><Icon name="Upload" size={13} /> Upload Image</>
              )}
            </button>
            <p className="text-xs text-gray-400 mt-1 text-center">or press Ctrl+V / ⌘V to paste</p>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Object Fit</label>
            <div className="flex gap-2">
              {['cover', 'contain', 'fill']?.map(fit => (
                <button
                  key={fit}
                  onClick={() => set('objectFit', fit)}
                  className={`flex-1 py-1.5 rounded-lg border text-xs font-medium capitalize transition-colors ${
                    (el?.objectFit || 'cover') === fit ? 'border-amber-500 bg-amber-50 text-amber-700' : 'border-gray-200 text-gray-600 hover:border-gray-300'
                  }`}
                >
                  {fit}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">
              Border Radius: <span className="text-amber-600">{el?.borderRadius || 0}px</span>
            </label>
            <input
              type="range" min={0} max={100} step={1}
              value={el?.borderRadius || 0}
              onChange={e => set('borderRadius', parseInt(e?.target?.value))}
              className="w-full accent-amber-500"
            />
          </div>
        </>
      )}
      {/* SHAPE properties */}
      {el?.type === 'shape' && (
        <>
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-2">Shape Type</label>
            <div className="grid grid-cols-2 gap-2">
              {SHAPE_TYPES?.map(s => (
                <button
                  key={s?.value}
                  onClick={() => set('shapeType', s?.value)}
                  className={`py-1.5 rounded-lg border text-xs font-medium transition-colors ${
                    (el?.shapeType || 'rectangle') === s?.value ? 'border-amber-500 bg-amber-50 text-amber-700' : 'border-gray-200 text-gray-600 hover:border-gray-300'
                  }`}
                >
                  {s?.label}
                </button>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-3">
            <label className="text-xs font-semibold text-gray-600 flex-shrink-0">Fill Color</label>
            <input
              type="color"
              value={el?.fill || '#f59e0b'}
              onChange={e => set('fill', e?.target?.value)}
              className="w-9 h-9 rounded-lg cursor-pointer border border-gray-300 p-0.5"
            />
            <input
              type="text"
              value={el?.fill || '#f59e0b'}
              onChange={e => { if (/^#[0-9a-fA-F]{0,6}$/?.test(e?.target?.value)) set('fill', e?.target?.value); }}
              className="flex-1 border border-gray-300 rounded-lg px-2 py-1.5 text-xs font-mono focus:outline-none focus:ring-2 focus:ring-amber-500"
            />
          </div>

          <div className="flex items-center gap-3">
            <label className="text-xs font-semibold text-gray-600 flex-shrink-0">Stroke</label>
            <input
              type="color"
              value={el?.stroke || '#ffffff'}
              onChange={e => set('stroke', e?.target?.value)}
              className="w-9 h-9 rounded-lg cursor-pointer border border-gray-300 p-0.5"
            />
            <div className="flex-1">
              <label className="block text-xs text-gray-500 mb-0.5">Width</label>
              <input
                type="number" min={0} max={20}
                value={el?.strokeWidth || 0}
                onChange={e => set('strokeWidth', parseInt(e?.target?.value) || 0)}
                className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-amber-500"
              />
            </div>
          </div>

          {(el?.shapeType === 'line') && (
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">
                Line Thickness: <span className="text-amber-600">{el?.lineThickness || 4}px</span>
              </label>
              <input
                type="range" min={1} max={40}
                value={el?.lineThickness || 4}
                onChange={e => set('lineThickness', parseInt(e?.target?.value))}
                className="w-full accent-amber-500"
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};

// ─── Background Panel ─────────────────────────────────────────────────────────
const BackgroundPanel = ({ backgroundColor, onBackgroundColorChange }) => {
  const bgColor = backgroundColor || '#0f172a';
  const isLight = ['#ffffff', '#f1f5f9', '#fafaf8']?.includes(bgColor);

  return (
    <div className="space-y-3">
      <div
        className="w-full h-10 rounded-lg border border-gray-200 flex items-center justify-center"
        style={{ backgroundColor: bgColor }}
      >
        <span className="text-xs font-mono" style={{ color: isLight ? '#374151' : '#ffffff' }}>
          {bgColor}
        </span>
      </div>
      <div>
        <label className="block text-xs font-semibold text-gray-600 mb-2">Presets</label>
        <div className="flex flex-wrap gap-2">
          {BG_PRESETS?.map(preset => (
            <button
              key={preset?.value}
              title={preset?.label}
              onClick={() => onBackgroundColorChange?.(preset?.value)}
              className={`w-7 h-7 rounded-lg border-2 transition-all hover:scale-110 ${
                bgColor === preset?.value ? 'border-amber-500 scale-110 shadow-md' : 'border-gray-300'
              }`}
              style={{ backgroundColor: preset?.value }}
            />
          ))}
        </div>
      </div>
      <div className="flex items-center gap-3">
        <label className="text-xs font-semibold text-gray-600 flex-shrink-0">Custom</label>
        <input
          type="color"
          value={bgColor}
          onChange={e => onBackgroundColorChange?.(e?.target?.value)}
          className="w-9 h-9 rounded-lg cursor-pointer border border-gray-300 p-0.5"
        />
        <input
          type="text"
          value={bgColor}
          onChange={e => {
            const val = e?.target?.value;
            if (/^#[0-9a-fA-F]{0,6}$/?.test(val)) onBackgroundColorChange?.(val);
          }}
          placeholder="#000000"
          className="flex-1 border border-gray-300 rounded-lg px-3 py-1.5 text-xs font-mono focus:outline-none focus:ring-2 focus:ring-amber-500"
        />
      </div>
    </div>
  );
};

// ─── Main SlideBuilder Component ──────────────────────────────────────────────
const SlideBuilder = ({ sections, onChange, projects, backgroundColor, onBackgroundColorChange }) => {
  // `sections` is repurposed as `elements` array for the new canvas-based editor
  const elements = sections || [];
  const [selectedId, setSelectedId] = useState(null);
  const [activePanel, setActivePanel] = useState('elements'); // 'elements' | 'background'

  const selectedEl = elements?.find(el => el?.id === selectedId) || null;

  const addElement = (type) => {
    const defaults = {
      text: {
        type: 'text',
        x: 80, y: 80, w: 400, h: 120,
        text: 'Double-click to edit',
        fontSize: 32,
        fontFamily: 'Inter, sans-serif',
        color: '#ffffff',
        align: 'center',
        vAlign: 'middle',
        bold: false, italic: false, underline: false, shadow: false,
        opacity: 1,
        zIndex: 1,
      },
      image: {
        type: 'image',
        x: 160, y: 70, w: 640, h: 400,
        url: '',
        objectFit: 'cover',
        borderRadius: 0,
        opacity: 1,
        zIndex: 1,
      },
      shape: {
        type: 'shape',
        x: 120, y: 120, w: 200, h: 80,
        shapeType: 'rectangle',
        fill: '#f59e0b',
        stroke: '#ffffff',
        strokeWidth: 0,
        opacity: 1,
        zIndex: 1,
      },
    };
    const newEl = { id: genId(), ...defaults?.[type] };
    const updated = [...elements, newEl];
    onChange(updated);
    setSelectedId(newEl?.id);
    setActivePanel('elements');
  };

  const updateElements = (updated) => {
    onChange(updated);
  };

  const updateSelected = (updated) => {
    const next = elements?.map(el => el?.id === updated?.id ? updated : el);
    onChange(next);
  };

  const deleteSelected = () => {
    onChange(elements?.filter(el => el?.id !== selectedId));
    setSelectedId(null);
  };

  const duplicateSelected = () => {
    if (!selectedEl) return;
    const dup = { ...selectedEl, id: genId(), x: selectedEl?.x + 20, y: selectedEl?.y + 20 };
    const next = [...elements, dup];
    onChange(next);
    setSelectedId(dup?.id);
  };

  const bringForward = () => {
    if (!selectedEl) return;
    const next = elements?.map(el => el?.id === selectedId ? { ...el, zIndex: (el?.zIndex || 1) + 1 } : el);
    onChange(next);
  };

  const sendBackward = () => {
    if (!selectedEl) return;
    const next = elements?.map(el => el?.id === selectedId ? { ...el, zIndex: Math.max(0, (el?.zIndex || 1) - 1) } : el);
    onChange(next);
  };

  return (
    <div className="space-y-3">
      {/* Canvas */}
      <div className="rounded-xl overflow-hidden border-2 border-gray-200 shadow-inner">
        <SlideCanvas
          elements={elements}
          backgroundColor={backgroundColor}
          selectedId={selectedId}
          onSelect={setSelectedId}
          onUpdateElement={updateElements}
          onDeselect={() => setSelectedId(null)}
        />
      </div>
      {/* Add Elements Toolbar */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide mr-1">Add:</span>
        {Object.entries(ELEMENT_TYPES)?.map(([type, info]) => (
          <button
            key={type}
            onClick={() => addElement(type)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border-2 border-dashed text-xs font-medium transition-all hover:scale-105 ${info?.color} border-current`}
          >
            <Icon name={info?.icon} size={13} />
            {info?.label}
          </button>
        ))}
        {selectedId && (
          <button
            onClick={() => setSelectedId(null)}
            className="ml-auto flex items-center gap-1 px-2 py-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-500 text-xs transition-colors"
          >
            <Icon name="X" size={12} /> Deselect
          </button>
        )}
      </div>
      {/* Panel Tabs */}
      <div className="border border-gray-200 rounded-xl overflow-hidden bg-white">
        <div className="flex border-b border-gray-200">
          <button
            onClick={() => setActivePanel('elements')}
            className={`flex-1 py-2.5 text-xs font-semibold flex items-center justify-center gap-1.5 transition-colors ${
              activePanel === 'elements' ? 'bg-amber-50 text-amber-700 border-b-2 border-amber-500' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <Icon name="Layers" size={13} />
            {selectedEl ? `${ELEMENT_TYPES?.[selectedEl?.type]?.label} Properties` : 'Properties'}
          </button>
          <button
            onClick={() => setActivePanel('background')}
            className={`flex-1 py-2.5 text-xs font-semibold flex items-center justify-center gap-1.5 transition-colors ${
              activePanel === 'background' ? 'bg-amber-50 text-amber-700 border-b-2 border-amber-500' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <Icon name="Palette" size={13} />
            Background
          </button>
          <button
            onClick={() => setActivePanel('layers')}
            className={`flex-1 py-2.5 text-xs font-semibold flex items-center justify-center gap-1.5 transition-colors ${
              activePanel === 'layers' ? 'bg-amber-50 text-amber-700 border-b-2 border-amber-500' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <Icon name="List" size={13} />
            Layers
          </button>
        </div>

        <div className="p-4">
          {activePanel === 'elements' && (
            <PropertiesPanel
              el={selectedEl}
              onUpdate={updateSelected}
              onDelete={deleteSelected}
              onDuplicate={duplicateSelected}
              onBringForward={bringForward}
              onSendBackward={sendBackward}
            />
          )}

          {activePanel === 'background' && (
            <BackgroundPanel
              backgroundColor={backgroundColor}
              onBackgroundColorChange={onBackgroundColorChange}
            />
          )}

          {activePanel === 'layers' && (
            <div className="space-y-1.5">
              {elements?.length === 0 ? (
                <p className="text-xs text-gray-400 text-center py-4">No elements yet. Add elements using the toolbar above.</p>
              ) : (
                [...elements]?.reverse()?.map((el, revIdx) => {
                  const idx = elements?.length - 1 - revIdx;
                  const info = ELEMENT_TYPES?.[el?.type];
                  const isSelected = el?.id === selectedId;
                  return (
                    <div
                      key={el?.id}
                      onClick={() => { setSelectedId(el?.id); setActivePanel('elements'); }}
                      className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer transition-colors ${
                        isSelected ? 'border-amber-400 bg-amber-50' : 'border-gray-200 hover:border-gray-300 bg-white'
                      }`}
                    >
                      <div className={`w-6 h-6 rounded flex items-center justify-center flex-shrink-0 ${info?.color}`}>
                        <Icon name={info?.icon || 'Square'} size={11} />
                      </div>
                      <span className="text-xs font-medium text-gray-700 flex-1 truncate">
                        {el?.type === 'text' ? (el?.text?.slice(0, 30) || 'Text') :
                         el?.type === 'image' ? (el?.url ? 'Image' : 'Image (empty)') :
                         `${el?.shapeType || 'Shape'}`}
                      </span>
                      <span className="text-xs text-gray-400">z:{el?.zIndex || 1}</span>
                      <button
                        onClick={(e) => { e?.stopPropagation(); onChange(elements?.filter(e2 => e2?.id !== el?.id)); if (selectedId === el?.id) setSelectedId(null); }}
                        className="w-5 h-5 rounded bg-red-50 text-red-400 hover:bg-red-100 flex items-center justify-center flex-shrink-0"
                      >
                        <Icon name="X" size={10} />
                      </button>
                    </div>
                  );
                })
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SlideBuilder;
